import React, {useEffect, useState} from 'react'
import styled, {createGlobalStyle} from 'styled-components'
import {useLayout} from 'organization/Event/Layout'
import {HideLiveChatSupport} from 'lib/WithLiveChatSupport'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import {format, parseISO} from 'date-fns'
import {
  Answer,
  useFormSubmissions,
} from 'lib/event-api/forms/use-form-submissions'
import {useForm} from 'organization/Event/Form/FormProvider'
import {useParams} from 'react-router-dom'
import {FormSubmissionStatus} from 'organization/Event/Form/FormModeration/Question'
import {useListenForFormSubmissionSavedUpdates} from 'organization/Event/Form/FormModeration'
import {ReactComponent as DeleteIcon} from 'assets/images/icons/delete-white.svg'
import Icon, {ShareIcon} from 'lib/ui/Icon'
import {sortSubmissions} from 'organization/Event/Form/FormModeration/QATab/sort-submissions'
import {useMoveSubmission} from 'organization/Event/Form/FormModeration/QATab/use-move-submission'
import IconButton from 'lib/ui/IconButton'
import ShareModal from 'organization/Event/Form/FormModeration/ShareModal'
import {useToggleArray} from 'lib/toggle'
import {useGuestVariables} from 'Event'
import {FeaturesBreadcrumbs} from 'organization/Event/Page/PageBreadcrumbs'

export default function StageView() {
  const {form} = useForm()
  const moveSubmission = useMoveSubmission({form})
  const {status} = useParams<{status: FormSubmissionStatus}>()

  const {data: submissions} = useFormSubmissions({
    form,
    status: status,
  })

  const {showFullScreen, setFullScreen} = useLayout()

  useEffect(() => {
    if (showFullScreen) {
      return
    }
    setFullScreen(true)
    return () => {
      if (showFullScreen) {
        setFullScreen(false)
      }
    }
  }, [showFullScreen, setFullScreen])

  const [zoom, setZoom] = useState<number>(1)
  const [showShareModal, setShowShareModal] = useToggleArray()

  const zoomIn = () => {
    setZoom((currentZoom) => currentZoom + 0.1)
  }

  const zoomOut = () => {
    setZoom((currentZoom) => currentZoom - 0.1)
  }

  const calculateZoom = (pixels: number) => pixels * zoom

  useListenForFormSubmissionSavedUpdates()

  if (!submissions) {
    return (
      <FeaturesBreadcrumbs page="Stage View">
        <HideLiveChatSupport>
          <FullPageLoader />
        </HideLiveChatSupport>
      </FeaturesBreadcrumbs>
    )
  }

  return (
    <FeaturesBreadcrumbs page="Stage View">
      <HideLiveChatSupport>
        <GlobalStyles />
        <Page>
          <PageHeader>
            <IconButton type="button" onClick={() => setShowShareModal()}>
              <IconBox>
                <ShareIcon color="#ffffff" iconSize={24} />
              </IconBox>
            </IconButton>
          </PageHeader>
          <ShareModal
            isVisible={showShareModal}
            onClose={() => setShowShareModal()}
          />
          {submissions.sort(sortSubmissions).map((submission, index) => {
            return (
              <Item aria-label="ondeck item" key={submission.id}>
                <ItemHead
                  style={{
                    fontSize: `${calculateZoom(25)}px`,
                  }}
                >
                  <ItemHeadLeft>
                    {`${submission.attendee_first_name} ${submission.attendee_last_name} @`}
                    &nbsp;
                    <ItemDate
                      style={{
                        fontSize: `${calculateZoom(20)}px`,
                      }}
                    >
                      {format(
                        parseISO(submission.saved_at),
                        'MMMM dd, yyy @ h:mm a',
                      )}
                    </ItemDate>
                  </ItemHeadLeft>
                </ItemHead>
                <Box>
                  <QuestionsPreview
                    answers={submission.answers}
                    calculateZoom={calculateZoom}
                  />
                  <StyledActionContent>
                    <DeleteIcon
                      style={{
                        cursor: 'pointer',
                        height: `${calculateZoom(30)}px`,
                      }}
                      onClick={() => {
                        moveSubmission({
                          fromStatus: status,
                          fromIndex: index,
                          toStatus: 'deleted',
                          toIndex: 0,
                        })
                      }}
                    />

                    <MoveToAnswered
                      className={'fa-solid fa-circle-check'}
                      iconSize={calculateZoom(30)}
                      color="#ffffff"
                      onClick={() => {
                        moveSubmission({
                          fromStatus: status,
                          fromIndex: index,
                          toStatus: 'answered',
                          toIndex: 0,
                        })
                      }}
                    />
                  </StyledActionContent>
                </Box>
              </Item>
            )
          })}

          <ZoomButtons>
            <ZoomButton onClick={zoomOut}>-</ZoomButton>
            <ZoomButton onClick={zoomIn}>+</ZoomButton>
          </ZoomButtons>
        </Page>
      </HideLiveChatSupport>
    </FeaturesBreadcrumbs>
  )
}

function QuestionsPreview(props: {
  answers: Answer[]
  calculateZoom: (pixels: number) => number
}) {
  const {answers, calculateZoom} = props
  const v = useGuestVariables()
  if (answers.length > 1) {
    return (
      <div>
        {answers.map((answer, index) => {
          if (!answer.answer) return null

          return (
            <div key={index}>
              <ItemQuestion
                style={{
                  fontSize: `${calculateZoom(30)}px`,
                }}
              >
                {v(answer.question)}
              </ItemQuestion>
              &nbsp;
              <ItemMessage
                style={{
                  fontSize: `${calculateZoom(30)}px`,
                }}
              >
                {answer.answer}
              </ItemMessage>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <ItemMessage
      style={{
        fontSize: `${calculateZoom(30)}px`,
      }}
    >
      {answers[0].answer}
    </ItemMessage>
  )
}

const ZoomButtons = styled.div`
  position: fixed;
  bottom: 15px;
  right: 15px;
  display: flex;
`

const ZoomButton = styled.div`
  width: 40px;
  height: 30px;
  background: #676767;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border-right: 1px solid #000000;

  &:last-child {
    border-right: 0px;
  }
`

const Page = styled.div`
  padding: ${(props) => props.theme.spacing[8]};
`

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const Item = styled.div`
  border-bottom: 1px dashed #d6d6d6;
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const ItemHead = styled.div`
  color: #ffffff;
  display: flex;
  justify-content: space-between;
`

const ItemHeadLeft = styled.div`
  align-items: center;
  display: flex;
  color: #d6d6d6;
`

const ItemDate = styled.span`
  font-style: italic;
`

const Box = styled.div`
  display: flex;
  justify-content: space-between;
`

const ItemQuestion = styled.span`
  color: #ffffff;
  font-weight: 600;
`
const ItemMessage = styled.span`
  font-weight: 400;
  color: #ffffff;
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const MoveToAnswered = styled(Icon)`
  margin-left: ${(props) => props.theme.spacing[4]};
  cursor: pointer;
`

const StyledActionContent = styled.div`
  display: flex;
  position: relative;
  bottom: ${(props) => props.theme.spacing[3]};
  float: right;
  align-self: flex-end;
`

const IconBox = styled.div`
  display: inline-flex;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  align-items: center;
  justify-content: center;
`

const GlobalStyles = createGlobalStyle`
body {
    background: #000000; 
}
`
